import { useRef, useState } from 'react';
import type React from 'react';

import { layoutStorage } from '../storage/deviceStorage';
import type { LayoutKey } from '../storage/deviceStorage';

type Direction = 'horizontal' | 'vertical';

/** How far one press of an arrow key moves the split, in percent of the whole. */
export const SPLIT_STEP = 5;

interface ResizableOptions {
  readonly initialSize?: number;
  readonly minSize?: number;
  readonly direction?: Direction;
  readonly layoutKey?: LayoutKey;
}

const clamp = (size: number, min: number, max: number): number => Math.min(max, Math.max(min, size));

/** The split this device remembers, when it is one: device storage holds whatever an older build or a person left. */
const rememberedSplit = (key: LayoutKey, fallback: number, min: number, max: number): number => {
  const stored = layoutStorage.read(key);
  const size = stored === null ? Number.NaN : Number(stored);
  return Number.isFinite(size) ? clamp(size, min, max) : fallback;
};

const ARROW_MOVES: Readonly<Record<Direction, Readonly<Record<string, number>>>> = {
  horizontal: { ArrowLeft: -SPLIT_STEP, ArrowRight: SPLIT_STEP },
  vertical: { ArrowUp: -SPLIT_STEP, ArrowDown: SPLIT_STEP },
};

/** Where a key moves the split; null for a key that does not move it. */
const sizeAfterKey = (key: string, size: number, direction: Direction, min: number, max: number): number | null => {
  if (key === 'Home') return min;
  if (key === 'End') return max;
  const move = ARROW_MOVES[direction][key];
  return move === undefined ? null : clamp(size + move, min, max);
};

/**
 * A two-panel split moved by mouse, finger, pen or keyboard. The pointer is captured by the separator,
 * so a drag needs no window listeners; the split is remembered once it is set, not on every move.
 */
export const useResizablePanels = ({
  initialSize = 50,
  minSize = 25,
  direction = 'vertical',
  layoutKey = 'panelSplit',
}: ResizableOptions = {}) => {
  const maxSize = 100 - minSize;
  const [panelSize, setPanelSize] = useState(() => rememberedSplit(layoutKey, initialSize, minSize, maxSize));
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const sizeAt = (event: React.PointerEvent): number | null => {
    const container = containerRef.current;
    if (container === null) return null;
    const rect = container.getBoundingClientRect();
    const span = direction === 'horizontal' ? rect.width : rect.height;
    if (span <= 0) return null;
    const offset = direction === 'horizontal' ? event.clientX - rect.left : event.clientY - rect.top;
    return clamp((offset / span) * 100, minSize, maxSize);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLElement>): void => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>): void => {
    if (!isDragging) return;
    const size = sizeAt(event);
    if (size !== null) setPanelSize(size);
  };

  const endDrag = (): void => {
    if (!isDragging) return;
    setIsDragging(false);
    layoutStorage.write(layoutKey, String(panelSize));
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    const size = sizeAfterKey(event.key, panelSize, direction, minSize, maxSize);
    if (size === null) return;
    event.preventDefault();
    setPanelSize(size);
    layoutStorage.write(layoutKey, String(size));
  };

  return {
    containerRef,
    panelSize,
    minSize,
    maxSize,
    isDragging,
    separatorHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onLostPointerCapture: endDrag,
      onKeyDown,
    },
  };
};
